/**
 * Mirrors of the C# Logistics › Stock DTO records, field for field.
 *
 * Its own file rather than more of `inventoryTypes.ts`, which already serves five modules: the
 * folder is what the suite rule is about, and a sixth and seventh module would double the
 * length of a file nobody could then see the shape of. The two are read together and neither
 * imports the other.
 */

import type { ListQuery } from '../../administration/users/userTypes';
import type {
  InventoryItemKind,
  LicensePaymentMode,
  LicensingModel,
  OwnershipType,
} from './inventoryTypes';

/** Enums serialize as int on the wire, so the numbers are the contract. */
export const BATCH_STATUS = {
  active: 0,
  quarantined: 1,

  /**
   * DERIVED, never stored.
   *
   * Nothing sweeps a lot into this state when its date passes — the server computes it on
   * every read and the facet filters on the same computation. A row's stored status is still
   * `active`; this is what the reader is shown. The number is held so a filter can name it.
   */
  expired: 2,

  depleted: 3,
  damaged: 5,
} as const;

export type StockBatchStatus = (typeof BATCH_STATUS)[keyof typeof BATCH_STATUS];

/**
 * The state of one serialized unit.
 *
 * <b>Assigned and Lent were declared on 20 Sep 2026, with the Issuances module that writes
 * them.</b> They were reserved until then on the rule this file already follows: a state
 * nothing can reach is a value in a picker and a facet that never counts anything. The
 * remaining gaps are still reserved — 3 and 4 are Sold and Transferred, 9 is
 * ReturnedToSupplier, and each waits on the record that produces it.
 */
export const UNIT_STATUS = {
  inStock: 0,
  assigned: 1,
  lent: 2,
  disposed: 5,
  damaged: 6,
  underRepair: 7,
  stolen: 8,
} as const;

export type StockUnitStatus = (typeof UNIT_STATUS)[keyof typeof UNIT_STATUS];

/**
 * What KIND of change a ledger line records.
 *
 * The gaps are the C# enum's own — `Disposal` is 5 — so the numbers are the contract, not the
 * order. A member added there and forgotten here shows as a blank label rather than a wrong one,
 * because `STOCK_MOVEMENT_TYPE_LABEL` is keyed on the union.
 */
export const STOCK_MOVEMENT_TYPE = {
  receipt: 0,
  adjustment: 1,

  /** Stock leaving on an issuance, and the return that credits it back. Always signed out. */
  issue: 3,

  disposal: 5,
} as const;

export type StockMovementType = (typeof STOCK_MOVEMENT_TYPE)[keyof typeof STOCK_MOVEMENT_TYPE];

/**
 * One label per movement type, in one place.
 *
 * The server has the same four words in `StockMovementService.MovementTypeLabel`, and that is
 * the one copy across the wire this product accepts — what it refuses is two copies on ONE side
 * of it, which is what put "Asset" in a form under a tab reading "Equipment".
 */
/**
 * What a ledger line is CALLED on screen, with an Adjustment's direction read off its sign.
 *
 * **Positive and Negative Adjustment are LABELS, not movement types.** The quantity is already
 * signed and the sign is what makes the ledger sum to the truth — a second encoding of the
 * direction as two enum members would be one fact in two slots, and a row could then be a
 * NegativeAdjustment holding +5 with nothing to stop it. Derived here, it cannot disagree.
 *
 * Every other type keeps its plain name: a Receipt is always in and a Disposal always out, so
 * their sign carries no information a label could add.
 */
export function movementTypeLabel(type: StockMovementType, quantity: number): string {
  if (type !== STOCK_MOVEMENT_TYPE.adjustment) return STOCK_MOVEMENT_TYPE_LABEL[type] ?? '';

  return quantity < 0 ? 'Negative Adjustment' : 'Positive Adjustment';
}

/**
 * The bare type name, without the direction. Used where a SET of types is being named — a facet
 * value has no one quantity to read a sign off.
 */
export const STOCK_MOVEMENT_TYPE_LABEL: Record<StockMovementType, string> = {
  [STOCK_MOVEMENT_TYPE.receipt]: 'Receipt',
  [STOCK_MOVEMENT_TYPE.adjustment]: 'Adjustment',
  [STOCK_MOVEMENT_TYPE.issue]: 'Issue',
  [STOCK_MOVEMENT_TYPE.disposal]: 'Disposal',
};

/**
 * One label per state, in one place.
 *
 * `active` reads as **Available** rather than Active, and that is not a coined word — it is the
 * enum's own gloss. It is necessary rather than cosmetic: the status facet on the same screen
 * uses Active for *not deleted*, so spelling this one the same way would put one word against
 * two meanings in two groups of one rail.
 */
export const BATCH_STATUS_LABEL: Record<StockBatchStatus, string> = {
  [BATCH_STATUS.active]: 'Available',
  [BATCH_STATUS.quarantined]: 'Quarantined',
  [BATCH_STATUS.expired]: 'Expired',
  [BATCH_STATUS.depleted]: 'Depleted',
  [BATCH_STATUS.damaged]: 'Damaged',
};

export const UNIT_STATUS_LABEL: Record<StockUnitStatus, string> = {
  [UNIT_STATUS.inStock]: 'In stock',

  /*
    `On loan` rather than "Lent", because that is the product's own word for it — Stock Levels
    already heads a column On loan, and one concept must not read two ways on two screens.
  */
  [UNIT_STATUS.assigned]: 'Assigned',
  [UNIT_STATUS.lent]: 'On loan',

  [UNIT_STATUS.disposed]: 'Disposed',
  [UNIT_STATUS.damaged]: 'Damaged',
  [UNIT_STATUS.underRepair]: 'Under repair',
  [UNIT_STATUS.stolen]: 'Stolen',
};

/**
 * Which states still HOLD the unit.
 *
 * Damaged and under repair count — broken and present is not the same fact as gone. It is the
 * line the ledger draws and the server sends `isOnHand` off the same split; this exists for the
 * form, which has to decide whether to offer Write off or Return to stock.
 */
export const UNIT_HELD: Record<StockUnitStatus, boolean> = {
  [UNIT_STATUS.inStock]: true,
  [UNIT_STATUS.damaged]: true,
  [UNIT_STATUS.underRepair]: true,
  [UNIT_STATUS.disposed]: false,
  [UNIT_STATUS.stolen]: false,

  /*
    OUT, and the line is PHYSICAL PRESENCE rather than ownership — `StockLedger.IsOnHand` says
    the same thing and this mirrors it. The organization still owns an issued laptop, so the
    test cannot be "do we have it": a damaged unit is in the store and an assigned one is not.
  */
  [UNIT_STATUS.assigned]: false,
  [UNIT_STATUS.lent]: false,
};

/** Out on an issuance — off the tally, and coming back by a return rather than a write-off. */
const UNIT_ON_ISSUANCE: Record<StockUnitStatus, boolean> = {
  [UNIT_STATUS.assigned]: true,
  [UNIT_STATUS.lent]: true,
  [UNIT_STATUS.inStock]: false,
  [UNIT_STATUS.damaged]: false,
  [UNIT_STATUS.underRepair]: false,
  [UNIT_STATUS.disposed]: false,
  [UNIT_STATUS.stolen]: false,
};

/**
 * Why a unit off the owned tally cannot be edited or written off, and what to do instead.
 *
 * <b>TWO remedies, because there are two ways to be off the tally and they are not the same
 * fact.</b> A unit somebody signed for is out on an issuance and comes back by recording a
 * return; a unit written off is gone, and only a theft is reversible. Before Issuances landed
 * there was one remedy and the sentence was written out at each call site — which is exactly
 * how a second state ends up described with the first one's words.
 *
 * One rule in one place, because the row menu and the profile cog both state it.
 */
export function unitBlockedReason(status: StockUnitStatus, action: 'edit' | 'write-off'): string {
  const label = UNIT_STATUS_LABEL[status].toLowerCase();

  if (UNIT_ON_ISSUANCE[status]) {
    return `This unit is ${label} — it is out on an issuance, so it comes back by recording a return rather than from here.`;
  }

  return action === 'edit'
    ? `This unit is ${label} and off the owned tally. Return it to stock before editing it.`
    : 'This unit is already off the owned tally.';
}

/** The states an ordinary edit may move a unit between. Off-tally states are the write-off. */
export const UNIT_HELD_STATUSES: StockUnitStatus[] = [
  UNIT_STATUS.inStock,
  UNIT_STATUS.damaged,
  UNIT_STATUS.underRepair,
];

/** The two a write-off may end in. Disposed is a disposal; stolen is an adjustment. */
export const UNIT_WRITE_OFF_STATUSES: StockUnitStatus[] = [
  UNIT_STATUS.disposed,
  UNIT_STATUS.stolen,
];

// ── Batches ──────────────────────────────────────────────────────────────────────────────

export interface StockBatchListItem {
  id: string;
  batchNumber: string;
  inventoryItemId: string;
  sku: string;
  itemName: string;
  unitCode: string;
  storageFacilityId: string;
  storageFacilityName: string;
  /** A calendar date (`YYYY-MM-DD`), never an instant. */
  expiryDate: string | null;
  quantityOnHand: number;
  quantityReceived: number;
  unitCost: number | null;
  /** Already carries the derived expiry — see `BATCH_STATUS.expired`. */
  status: StockBatchStatus;
  isExpired: boolean;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface StockBatchDetail extends StockBatchListItem {
  quarantineReason: string | null;
  notes: string | null;
}

export interface CreateStockBatchRequest {
  inventoryItemId: string;
  storageFacilityId: string;
  batchNumber: string;
  /** Written to both quantity columns by the ledger's opening receipt. */
  quantity: number;
  expiryDate: string | null;
  unitCost: number | null;
  notes: string | null;
}

/**
 * Which way an adjustment moves the quantity. The server holds the same two members.
 *
 * **It replaced the COUNTED quantity on 21 Sep 2026**, and that is a product call reversing
 * what this form shipped with: the reader gave a total and the server subtracted. They give the
 * change now. The job people actually bring here is correcting an entry added in error, where
 * the change is known and the total is not — and a reader typed 30 meaning "take 30 off" while
 * the form read it as "the shelf holds 30".
 *
 * **Asked for rather than derived from the reason.** That argument is history now and is kept
 * because it is what the decision rested on: four of the five reasons implied a sign and
 * `countAdjustment` did not, because a recount lands either way, so deriving would have meant
 * splitting that member in two or dropping it — a vocabulary change to avoid one question.
 * Hours later the reason became free text, and a string implies nothing at all: this control is
 * the ONLY thing that decides the sign.
 */
export const ADJUSTMENT_DIRECTION = {
  add: 0,
  deduct: 1,
} as const;

export type StockAdjustmentDirection =
  (typeof ADJUSTMENT_DIRECTION)[keyof typeof ADJUSTMENT_DIRECTION];

/** The server holds the same two strings in `StockAdjustmentDirections.Label`. */
export const ADJUSTMENT_DIRECTION_LABEL: Record<StockAdjustmentDirection, string> = {
  [ADJUSTMENT_DIRECTION.add]: 'Add',
  [ADJUSTMENT_DIRECTION.deduct]: 'Deduct',
};

/** What each direction does, shown under the option. */
export const ADJUSTMENT_DIRECTION_DESCRIPTION: Record<StockAdjustmentDirection, string> = {
  [ADJUSTMENT_DIRECTION.add]: 'More stock than the record says. Opens the lot if none exists.',
  [ADJUSTMENT_DIRECTION.deduct]: 'Less stock than the record says.',
};

/**
 * The SIGNED delta, converted once — the mirror of `StockAdjustmentDirections.Apply`.
 *
 * Every derivation on the form reads this rather than negating by hand, so no two of them can
 * disagree about which way a deduction goes.
 */
export function applyDirection(direction: StockAdjustmentDirection, quantity: number): number {
  return direction === ADJUSTMENT_DIRECTION.deduct ? -quantity : quantity;
}

/**
 * What the Reason field SUGGESTS — and it is a suggestion, not a set.
 *
 * <b>It was an enum on both sides of the wire until 21 Sep 2026.</b> Product call: the
 * reason is free text now, because once the DIRECTION alone decides the sign there is
 * nothing about the reason that changes what the system does — and a fixed list of four
 * words was refusing sentences that were simply true. The server takes any string.
 *
 * These four are kept because a picker that opens empty makes the reader invent a
 * vocabulary one row at a time, which is how five spellings of one reason arrive. They are
 * offered, never enforced.
 *
 * <b>The cost is the aggregation test</b>, and it is stated on the server DTO: free text
 * drifts, so nothing can group by this. Nothing does today. The day something must, this
 * becomes a catalog rather than a wider enum.
 *
 * <b>"Location/Storage Adjustment" is deliberately NOT among them</b>, which the retired enum's
 * own comment carried and which survives the move: stock found at another facility has not
 * changed in quantity, it has MOVED — two movements at two facilities. Filing it here takes the
 * stock out of one facility's count and puts it nowhere. Nothing STOPS a reader typing those
 * words now; what the list can still do is decline to suggest them.
 */
export const ADJUSTMENT_REASON_SUGGESTIONS: readonly string[] = [
  'Count adjustment',
  'Missed / uncounted item',
  'Found item addition',
  'Duplicate entry',
];
/**
 * Correcting what a lot is recorded as holding, against what was physically counted.
 *
 * **It REPLACED the restock request on 21 Sep 2026, and the difference is the movement it
 * writes.** Restocking received a delivery — a `Receipt` that added to on hand and to the
 * running total of what had ever arrived. This says the RECORD was wrong: a signed
 * `Adjustment`, and `quantityReceived` is left alone because nothing arrived.
 *
 * **It carries the CHANGE, not a total**, and that reverses what this line said until
 * 21 Sep 2026. The old shape took the counted quantity and had the server subtract what was
 * recorded — right for a stocktake, wrong for the correction people actually bring here. See
 * {@link ADJUSTMENT_DIRECTION}.
 */
export interface StockAdjustmentRequest {
  inventoryItemId: string;
  storageFacilityId: string;
  /**
   * Which lot is being adjusted, or null for "I do not know".
   *
   * Null is answerable only on a DEDUCT — the server draws it off the lots that exist, first to
   * expire first. An ADD has to name its lot, because one cannot be opened without a number.
   */
  batchNumber: string | null;
  /** How much is added or taken off. UNSIGNED — the direction carries the sign. */
  quantity: number;
  /** Whether the quantity is added to the lot or taken off it. */
  direction: StockAdjustmentDirection;
  /** Why the stock moved, in the reader's own words. Free text — see the suggestions. */
  reason: string;
  /** Required where the item tracks expiry, and only read when a lot is created. */
  expiryDate: string | null;

  /*
    NO UNIT COST. Removed 22 Sep 2026, reversing the day-old call that made it required: an
    adjustment corrects a QUANTITY and says nothing about what the stock is worth, so asking
    produced a figure somebody invented to get past the validator. A lot OPENED by an
    adjustment is unpriced, which is the honest null — pricing belongs to whatever receives it.
  */
  notes: string | null;
}

export interface UpdateStockBatchRequest {
  batchNumber: string;
  /** The corrected figure, not a delta. The server computes the adjustment. */
  quantityOnHand: number;
  expiryDate: string | null;
  unitCost: number | null;
  status: StockBatchStatus;
  quarantineReason: string | null;
  notes: string | null;
  /** Required by the server when the quantity actually moved. */
  adjustmentReason: string | null;
}

/** Positive — how much to destroy. The ledger line written from it is negative. */
export interface DisposeStockRequest {
  quantity: number;
  reason: string;
}

export interface StockBatchListQuery extends ListQuery {
  item?: string;
  facility?: string;
  condition?: string;
}

// ── Serialized units ─────────────────────────────────────────────────────────────────────

export interface StockUnitListItem {
  id: string;
  serialNumber: string;
  inventoryItemId: string;
  sku: string;
  itemName: string;
  /**
   * Where it is. NULL for a unit that is not anywhere.
   *
   * A licence seat sits nowhere — see `StockUnit.StorageFacilityId` on the server. Null is not
   * "unknown": it is "does not apply", and the column renders an em dash rather than a blank.
   */
  storageFacilityId: string | null;
  storageFacilityName: string | null;
  ownership: OwnershipType | null;
  acquisitionDate: string | null;

  /** The property number — INV-{yyyy}-{NNNNN}. Server-assigned; no request carries one. */
  inventoryNumber: string | null;

  /** The funding source this unit was bought under. */
  fund: string | null;
  leaseEndDate: string | null;
  /**
   * Who it is leased FROM. Leased units only.
   *
   * Free text, and a REVERSAL of a deferral this product recorded twice — see
   * `StockUnit.LeasedFrom` on the server, which carries the product call. A lessor is almost
   * always an organization, which is what keeps it out of the identity split.
   */
  leasedFrom: string | null;
  termEndDate: string | null;
  unitCost: number | null;
  status: StockUnitStatus;

  /**
   * The issuance this unit is OUT on, where it is out — so the row can offer a return.
   *
   * A return is recorded against the SLIP under `issuances.return`, and the status alone says a
   * unit is Assigned or Lent rather than which issuance put it there.
   *
   * **The number travels and the HOLDER does not.** An Assign's holder is a `PersonnelId` into
   * `rsys_pii`, and a name on a list row is the copy the identity split exists to prevent — so
   * the reference's own HOLDER column is the one part of its grid this product cannot carry.
   */
  openIssuanceId: string | null;

  openIssuanceNumber: string | null;

  /**
   * The unit's own standing note — what the registration grid collected for this row.
   *
   * **Not the ledger line's reason.** A movement's `reason` says why stock MOVED and is
   * generated for a receipt (stock arriving IS the reason); this is a standing note about the
   * thing. The adjustment form carries both, which is what makes the split visible.
   */
  notes: string | null;
  /** Whether it counts toward the quantity on hand. Sent, never re-derived on the client. */
  isOnHand: boolean;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface StockUnitDetail extends StockUnitListItem {
  statusReason: string | null;
  notes: string | null;

  /**
   * Who the seat is assigned to — an ID, never a name.
   *
   * The profile resolves it through `personsApi.resolve` for the ONE record on screen, exactly
   * as a real property's custodian is resolved. A name on this shape would be the copy the
   * identity split exists to prevent, which is why it is not on the LIST row at all: a list
   * would mean resolving a page of them, and sorting one would mean resolving the whole table.
   */
  accountablePersonnelId: string | null;

  /** How the seat is billed. Licences only. */
  paymentMode: LicensePaymentMode | null;

  /** When the first invoice falls due. Recurring modes only. */
  firstBillingDate: string | null;
}

export interface CreateStockUnitRequest {
  inventoryItemId: string;
  /** Required where the kind sits somewhere, and REFUSED where it does not. */
  storageFacilityId: string | null;
  serialNumber: string;
  ownership: OwnershipType | null;
  acquisitionDate: string | null;

  /** The funding source this unit was bought under. */
  fund: string | null;
  leaseEndDate: string | null;
  /**
   * Who it is leased FROM. Leased units only.
   *
   * Free text, and a REVERSAL of a deferral this product recorded twice — see
   * `StockUnit.LeasedFrom` on the server, which carries the product call. A lessor is almost
   * always an organization, which is what keeps it out of the identity split.
   */
  leasedFrom: string | null;
  termEndDate: string | null;
  unitCost: number | null;
  notes: string | null;
}

/**
 * One serialized thing inside a registration — the row the reader typed.
 *
 * **The FACILITY is here and the TENURE is not.** Three laptops out of one purchase order can
 * be stored in three buildings, so where a unit sits is a fact about the unit; they cannot have
 * been bought under three different agreements, because one registration is one purchase or one
 * lease. The item is the header's too — a registration cannot mix two catalog items.
 */
/**
 * What an ABANDONED row sends for its facility.
 *
 * <b>Not an empty string, and this is a real 400 rather than a nicety.</b> The server drops a row
 * nobody touched and refuses one somebody started, and it tells them apart by
 * `StorageFacilityId == Guid.Empty`. `System.Text.Json` cannot deserialize `""` into a `Guid` at
 * all, so a grid sending one makes the WHOLE request fail model binding before that rule is
 * reached — the reader is told nothing useful, and the abandoned-row handling they were relying
 * on never runs.
 *
 * Verified against `System.Text.Json` rather than assumed: `""` throws
 * <i>"The JSON value could not be converted to System.Guid"</i>.
 */
export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

export interface RegisterStockUnitLine {
  /** Required where the kind sits somewhere, and REFUSED where it does not. */
  storageFacilityId: string | null;
  /** The serial — a LICENCE NUMBER where the item is a digital asset. */
  serialNumber: string;
  acquisitionDate: string | null;

  /** The funding source this unit was bought under. */
  fund: string | null;
  /** Licence or service term end — subscriptions only. */
  termEndDate: string | null;

  /**
   * When the lease on THIS unit ends. Leased registrations only.
   *
   * It moved off the HEADER on 28 Sep 2026: a hire agreement whose vans come back on
   * staggered dates is the ordinary case, and the unit column was per unit all along. The
   * TENURE is still the header’s — a mixed delivery is two registrations.
   */
  leaseEndDate: string | null;

  /**
   * Who it is leased FROM. Leased units only.
   *
   * Free text, and a REVERSAL of a deferral this product recorded twice — see
   * `StockUnit.LeasedFrom` on the server, which carries the product call. A lessor is almost
   * always an organization, which is what keeps it out of the identity split.
   */
  leasedFrom: string | null;
  /**
   * The employee this seat is assigned to. An ID only — a name never travels on this row.
   *
   * The server checks the personnel record is live; there is no foreign key doing it, because
   * nothing in `rsys_` may join a name onto a stock row.
   */
  accountablePersonnelId: string | null;
  unitCost: number | null;
  /** How the seat is billed. Refused on anything but a digital asset. */
  paymentMode: LicensePaymentMode | null;
  /** When the first invoice falls due. Refused unless the mode is recurring. */
  firstBillingDate: string | null;
  notes: string | null;
}

/**
 * Receiving SEVERAL serialized things of one item, in one transaction.
 *
 * **ALL-OR-NOTHING.** A delivery is one event, so a registration that wrote eight units and
 * refused the ninth would leave the reader working out which four are missing and re-keying
 * them against a serial check that now refuses the eight already in. A refusal names the row
 * as `units.{index}.{field}`, which is what lets the grid point at the cell.
 *
 * It writes one `Receipt` per unit, exactly as the single create does — not the adjustment the
 * quantity side records, which says a FIGURE was wrong rather than that stock arrived.
 */
export interface RegisterStockUnitsRequest {
  inventoryItemId: string;
  /**
   * Header-scoped: every unit in the registration shares it. Null reads as owned.
   *
   * **REFUSED on a digital asset**, where the server derives it from the licensing model —
   * perpetual is owned, a term is leased. Asking a second time would be one fact in two slots.
   */
  ownership: OwnershipType | null;

  /*
    NO HEADER LEASE END. The date and the lessor are both PER ROW from 28 Sep 2026 — three
    rented vans come back on three days and can come from three hire companies, which a
    header-scoped pair could not record. The TENURE stays here: a mixed delivery is still
    two registrations.
  */

  units: RegisterStockUnitLine[];
}

export interface UpdateStockUnitRequest {
  serialNumber: string;
  /** A unit moves between facilities; a batch does not. Null where it is not anywhere. */
  storageFacilityId: string | null;
  ownership: OwnershipType | null;
  acquisitionDate: string | null;

  /** The funding source this unit was bought under. */
  fund: string | null;
  leaseEndDate: string | null;
  /**
   * Who it is leased FROM. Leased units only.
   *
   * Free text, and a REVERSAL of a deferral this product recorded twice — see
   * `StockUnit.LeasedFrom` on the server, which carries the product call. A lessor is almost
   * always an organization, which is what keeps it out of the identity split.
   */
  leasedFrom: string | null;
  termEndDate: string | null;
  unitCost: number | null;
  /** Only a state that still HOLDS the unit. Taking it off the tally is the write-off. */
  status: StockUnitStatus;
  statusReason: string | null;
  notes: string | null;
}

export interface WriteOffStockUnitRequest {
  /** Disposed or stolen. Every other value is refused. */
  status: StockUnitStatus;
  reason: string;
}

/** Optional reason: putting something back is the undo, and ceremony on an undo is noise. */
export interface ReturnStockUnitRequest {
  reason: string | null;
}

export interface StockUnitListQuery extends ListQuery {
  item?: string;
  facility?: string;
  condition?: string;
}

// ── Stock levels ──────────────────────────────────────────────────────────────────────

/**
 * How much of an item is available, against its two thresholds.
 *
 * DERIVED from `Available`, never stored — an item holding forty quarantined units is out of
 * stock for every purpose a reorder level serves, which is why the arithmetic runs on what
 * could be issued rather than on what is held.
 */
export const STOCK_STATUS = {
  outOfStock: 0,
  low: 1,
  adequate: 2,

  /** Only reachable for an item that declares a maximum. One with none is never over it. */
  over: 3,

  /**
   * The item holds no stock at all, so none of the four above means anything for it.
   *
   * **Real property, and nothing else today.** A building is not received into a lot and not
   * named as a unit, so every quantity on its row is a true zero — and the arithmetic would
   * otherwise read that as OUT OF STOCK, putting a red dot and a red pill on an office block
   * on a screen readers scan for shortages.
   */
  notTracked: 4,
} as const;

export type StockStatus = (typeof STOCK_STATUS)[keyof typeof STOCK_STATUS];

export const STOCK_STATUS_LABEL: Record<StockStatus, string> = {
  [STOCK_STATUS.outOfStock]: 'Out of stock',
  [STOCK_STATUS.low]: 'Low',
  [STOCK_STATUS.adequate]: 'Adequate',
  [STOCK_STATUS.over]: 'Over maximum',
  [STOCK_STATUS.notTracked]: 'Not tracked',
};

/**
 * The `<StatusText>` tone one stock status renders in.
 *
 * Beside the labels rather than on either surface that draws it, because TWO now do — the Stock
 * Levels column and the stock detail panel behind its anchor. A copy on each is two mappings
 * for one state, and the one that drifts is the one nobody is looking at: a reader clicking a
 * row would meet a red pill in the list and an amber one in the panel, with both files
 * internally consistent.
 *
 * **NO AMBER**, which is this product's own rule rather than a palette choice: amber is the
 * brand colour, so on a record it reads as chrome the shell put there rather than as a fact
 * about the item.
 */
/**
 * What tone a UNIT's condition reads in — one mapping, and two surfaces draw it.
 *
 * <b>No amber</b>, which is the index-page rule: amber is the brand colour here, so on a row
 * it reads as chrome rather than as a fact about the unit. Anything off the owned tally or out
 * of service is `blocked`; only a unit in stock is `active`.
 *
 * It lived in `<StockUnitsIndexPage>` until the stock detail panel needed the same pill, which
 * is the move `stockStatusTone` already made and for the same reason: a copy on each surface is
 * the mapping that drifts unseen — a reader would meet one tone in the list and another in the
 * panel, with both files internally consistent.
 */
export function unitConditionTone(status: StockUnitStatus): 'active' | 'blocked' {
  return status === UNIT_STATUS.inStock ? 'active' : 'blocked';
}

export function stockStatusTone(status: StockStatus): 'active' | 'blocked' | 'inactive' {
  if (status === STOCK_STATUS.outOfStock || status === STOCK_STATUS.low) return 'blocked';

  // Not a state to act on and not a healthy one either — the slate the product already uses
  // for a row that is simply not in play.
  if (status === STOCK_STATUS.notTracked) return 'inactive';

  return 'active';
}

/**
 * One item's stock, derived.
 *
 * **Nothing here is a record.** Every figure is computed at read time from the batches, the
 * units and the ledger, so there is no create, no update and no delete — and `id` is the
 * ITEM's, because a level is a view of an item rather than a row of its own.
 */
export interface StockLevelListItem {
  /** The ITEM's id. What the anchor opens, and what the row is keyed on. */
  id: string;
  sku: string;
  name: string;

  /**
   * The maker, the make and the model — what the CHILD row is titled by.
   *
   * The list groups by item type, so the group row already states the `name`; a child titled
   * by it repeats its own parent, which a one-item group made obvious. Brand and model are
   * what separate two items of one governed name.
   *
   * All three are optional and a row may carry any of them or none — see `itemTitle`.
   */
  manufacturer: string | null;
  /** @see manufacturer */
  brandName: string | null;
  /** @see manufacturer */
  model: string | null;

  /**
   * The item TYPE this row files under — what the list GROUPS by.
   *
   * Sent because a group row acts on the catalog row itself: it adds an item of that type and
   * retires the type. A group keyed on the name alone could render but not act.
   */
  inventoryItemTypeId: string;

  /**
   * The CATEGORY this row files under — what `inventoryCategoryName` resolves through.
   *
   * Sent for the same reason `inventoryItemTypeId` is, one field along: the group row's
   * "Add item" seeds the new item's category from what is already under that group, and a
   * category known only by its NAME can be displayed but not bound to a picker.
   */
  inventoryCategoryId: string;

  inventoryCategoryName: string;
  kind: InventoryItemKind;

  /**
   * How a subscription is licensed. Null on every other kind.
   *
   * On the row because the licence registration reads it the moment it opens and has only this
   * row to hand — the same argument `tracksSerial` makes. It decides the TENURE, which that form
   * derives rather than asks for, and whether a term-end column is drawn at all.
   */
  licensingModel: LicensingModel | null;

  unitCode: string;

  /**
   * Whether this item's stock is NAMED one row at a time rather than counted.
   *
   * On the row because it changes what every figure beside it MEANS: for a serialized item a
   * quantity is a count of units, and for a quantity item it is a sum of lot balances.
   */
  tracksSerial: boolean;

  /** Null where nothing anywhere carries a price — a different fact from a value of zero. */
  investment: number | null;
  /**
   * EVERY unit on the books, whatever its status — what the Summary band adds up to.
   *
   * `registeredUnits = available + unusable + issued + writtenOff`, which is the identity a
   * reader does in their head. It is NOT `onHand`: on-hand excludes issued stock, so putting
   * that figure at the head of the band made the row contradict itself — 4 registered beside
   * 3 issued and 1 unusable, with 3 available.
   */
  registeredUnits: number;
  onHand: number;
  /** On hand MINUS unusable. The figure the status is computed from. */
  available: number;
  unusable: number;
  writtenOff: number;

  expired: number;
  damaged: number;
  underRepair: number;
  quarantined: number;

  condemned: number;
  stolen: number;

  /**
   * Out with an external party on a lend, and not yet back. OUTSTANDING, never cumulative.
   *
   * Real from 21 Sep 2026 — this band carried no field at all before the Issuances module, and
   * its columns rendered an em dash rather than a zero the server had invented.
   */
  onLoan: number;

  /** In a staff member's custody against an ICS or a PAR. @see onLoan */
  assigned: number;

  /**
   * Out on a lend past the day it was due back.
   *
   * A SUBSET of {@link onLoan}, never a separate pool — every delayed unit is also on loan, so
   * the two are not added together.
   */
  delayedReturn: number;

  /**
   * How much has been DISPENSED — handed over to be used up.
   *
   * <b>CUMULATIVE, and the only figure in its band that is.</b> Everything beside it answers
   * where stock is NOW; a dispense is consumption, so nothing comes back and what has ever
   * left is the only honest answer. It only ever rises.
   *
   * <b>Which is why the column draws on CONSUMABLES alone.</b> It was an em dash on every tab
   * for exactly that reason — a running total under a heading of current holdings is the one
   * column there that would mean something different. Dropping it from the other four tabs is
   * what made it answerable rather than misleading.
   */
  dispensed: number;

  /**
   * Delivered against a purchase order and NOT YET STOCK — sitting on an open goods receipt.
   *
   * **Product call, 01 Oct 2026.** Stock is created at REGISTRATION, and registration is
   * refused while any line is uninspected — so no uninspected quantity has ever reached a lot
   * or a unit, and every other figure on this row was already correct. What was missing is that
   * the quantity on the dock appeared NOWHERE.
   *
   * **It is NOT the reference's "For Delivery".** That column is open PO quantity still on its
   * way from the supplier — the stage BEFORE delivery, and not built here. This one is the
   * stage after: delivered, awaiting the official verdict.
   *
   * **Outside `registeredUnits` and outside the Investment money**, deliberately: that band
   * tallies what is owned AS STOCK, and a delivery nobody has verified is not that yet.
   *
   * **It does NOT move {@link stockStatus}.** An item whose only stock is on the dock still
   * reads "Out of stock" and still draws the red dot — a product call, taken with the cost
   * stated rather than discovered.
   */
  forInspection: number;

  nearExpiry: number;
  /** The window the figure beside it was computed with. PER ITEM, so it travels in the cell. */
  nearExpiryDays: number;

  /**
   * Units of this item whose lease ends inside the warning window and has NOT lapsed.
   *
   * A COUNT rather than a date, because `StockUnit.LeaseEndDate` is per unit — three rented
   * vans of one model come back on three different days, and this row is per ITEM.
   */
  leaseExpiring: number;

  /**
   * Units whose lease has already LAPSED and which are still on hand.
   *
   * Never folded into the figure above: there is still time to renew one and none to renew
   * the other, which is the same split the expired / near-expiry pair draws one band along.
   */
  leaseExpired: number;

  /** The soonest lease end among this item's units, lapsed ones included. A calendar date. */
  earliestLeaseEnd: string | null;

  /** The window the two figures above used. Flat, and NOT the item's expiry threshold. */
  leaseEndingDays: number;

  /**
   * Live properties registered under this item.
   *
   * **The one figure a REAL-PROPERTY row can state**, and the reason its tab exists: every
   * quantity column is a true zero for that kind, so the tab would otherwise be a band of zeros
   * claiming the organization holds no buildings.
   *
   * Zero on every other kind, and the column is withheld from their tabs rather than rendered —
   * a consumable has no properties, and a column of zeros saying so is a fact about the product
   * dressed as a fact about the stock.
   */
  registeredProperties: number;

  /**
   * How much of this item is held on a LEASE rather than owned — units and registered
   * properties together.
   *
   * **It replaces the Leased & Rented TAB**, 06 Oct 2026 — product call, in the reader's own
   * words: *remove the Leases & rented, instead make it a column and a card*. A tab that
   * crossed the kind and tenure axes could only be read by leaving whichever tab held the
   * kind; a figure on the row answers the same question from wherever the reader is.
   *
   * It counts the THING rather than the catalog row: a tenure is recorded per unit and per
   * property, so an item whose own row says Owned still reports the one rented van under it —
   * which the tab could not. Stored, because nothing about it moves at midnight.
   */
  leased: number;

  reorderLevel: number;
  maxStockLevel: number | null;
  stockStatus: StockStatus;

  /** An INSTANT, off the ledger. Null for an item nothing has ever moved. */
  latestMovementAt: string | null;

  /**
   * Set only on the SYNTHETIC row a group's children are summed into. **The server never
   * sends it**, so a real row never carries it.
   *
   * It exists because not every column can be totalled: a reorder level is a property of ONE
   * item, and two of them added together produce a number nothing reorders against. Those
   * columns read the flag and render an em dash rather than a figure the group cannot own.
   */
  isGroupTotal?: true;

  /**
   * The group’s figures have NOT arrived, so nothing on this row may be rendered as a value.
   *
   * A group placeholder is built from `GET /api/stock-levels/groups` — which answers with a
   * type, a name and a count, and no figures at all — so every numeric field on it is a zero
   * the row type requires rather than a total anybody computed.
   *
   * <b>Those zeros are unreachable, and that is enforced by ONE branch rather than by care.</b>
   * `<GroupRow>` renders a skeleton for every cell but the first until its figures land, and
   * the row the columns are finally handed is built by `levelFromGroupFigures` from the
   * server’s own sums. A column render is never called with a pending row.
   *
   * **The server never sends it**, like `isGroupTotal` beside it.
   */
  figuresPending?: boolean;

  /**
   * How many items are filed under this TYPE — on a group row only.
   *
   * The count the reader sees beside a collapsed group’s name, and the number of placeholder
   * rows drawn while its children load. It is the SERVER’s, so it is right before a single
   * child has been fetched — which is the whole reason a group can state what expanding will
   * show without having loaded it.
   */
  groupItemCount?: number;
}

/**
 * The stock-levels list query.
 *
 * **There is no `status` axis**, and its absence is a decision: a level is a view of an ACTIVE
 * item, so there is nothing deleted to file by. The folder nav uses `kind` instead.
 */
/**
 * One ITEM TYPE, with how many items are filed under it — and no figures at all.
 *
 * The second of four progressive calls, and the one that makes the table appear. A type is
 * what the table groups under, so this is every row before anything is expanded: the name a
 * reader scans for, and the count that says whether opening it will show something.
 *
 * The FIGURES arrive per row from `groupFigures`, so a slow group fills in on its own rather
 * than holding the other nine behind it.
 */
export interface StockLevelGroupRow {
  /**
   * What the group IS — an item TYPE, or a CATEGORY on the Real Property tab.
   *
   * Named for the ROLE rather than the column, because the two axes answer the same question
   * on different tabs and a field called `inventoryItemTypeId` carrying a category id would
   * be a label describing an inference. Which one it is travels on the query as `groupBy`.
   */
  groupId: string;
  name: string;
  itemCount: number;
}

/**
 * What a GROUP can honestly state — the sum of its children, and nothing borrowed.
 *
 * **The absences are the contract.** No reorder level, no maximum, no near-expiry window and
 * no lease-ending window, because a THRESHOLD is per item: two reorder levels added together
 * produce a number nothing reorders against.
 *
 * The table used to build this row by spreading its FIRST CHILD and overriding what it
 * summed, so every column nobody remembered to override was silently that one item’s value
 * presented as the group’s. It read correctly on a group of one, which is why it survived.
 */
export interface StockLevelGroupFigures {
  /** @see StockLevelGroupRow.groupId */
  groupId: string;

  /** Summed over the PRICED rows only. Null where no child was priced — an em dash, not a zero. */
  investment: number | null;

  /**
   * EVERY unit on the books, whatever its status — what the Summary band adds up to.
   *
   * `registeredUnits = available + unusable + issued + writtenOff`, which is the identity a
   * reader does in their head. It is NOT `onHand`: on-hand excludes issued stock, so putting
   * that figure at the head of the band made the row contradict itself — 4 registered beside
   * 3 issued and 1 unusable, with 3 available.
   */
  registeredUnits: number;

  onHand: number;
  available: number;
  unusable: number;
  writtenOff: number;

  expired: number;
  damaged: number;
  underRepair: number;
  quarantined: number;

  condemned: number;
  stolen: number;

  onLoan: number;
  assigned: number;
  delayedReturn: number;

  /**
   * How much has been DISPENSED — handed over to be used up.
   *
   * <b>CUMULATIVE, and the only figure in its band that is.</b> Everything beside it answers
   * where stock is NOW; a dispense is consumption, so nothing comes back and what has ever
   * left is the only honest answer. It only ever rises.
   *
   * <b>Which is why the column draws on CONSUMABLES alone.</b> It was an em dash on every tab
   * for exactly that reason — a running total under a heading of current holdings is the one
   * column there that would mean something different. Dropping it from the other four tabs is
   * what made it answerable rather than misleading.
   */
  dispensed: number;

  /**
   * What is awaiting inspection across the group.
   *
   * Summed rather than refused: a QUANTITY adds, which is the test this record is built on —
   * it is the thresholds beside it that a group has to answer with an em dash.
   *
   * @see StockLevelListItem.forInspection
   */
  forInspection: number;

  nearExpiry: number;

  leaseExpiring: number;
  leaseExpired: number;

  /** The soonest among the children — a MINIMUM, and it includes a lapsed one. */
  earliestLeaseEnd: string | null;

  registeredProperties: number;

  /** A real SUM — a unit and a property each count one, so adding two groups adds things. */
  leased: number;

  /** The WORST among the children, never the first one’s. */
  stockStatus: StockStatus;

  latestMovementAt: string | null;
}

export interface StockLevelListQuery {
  /**
   * ONE GROUP — the CHILDREN of it, asked for when the reader expands it.
   *
   * Not a facet: it names a group they have just opened rather than an axis anybody narrows
   * the list on, which is the same reading `pinnedOnly` takes one suite along.
   */
  group?: string;

  /**
   * WHICH AXIS the table groups on — `category` on the Real Property tab, `type` everywhere
   * else (and whenever this is absent).
   *
   * **It changes what `group` matches**, which is why the two travel together: a group id is
   * a type id under one and a category id under the other. A property item's type is the
   * seeded `Real Property` row, so grouping that tab by type puts every property on the books
   * into one group named after the tab itself — and with the Property Name off the form, the
   * category is the only thing that tells two property items apart.
   */
  groupBy?: 'type' | 'category';

  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;

  /** `InventoryItemKind` names, comma-separated. The folder-nav axis. */
  kind?: string;
  /** Category NAMES, comma-separated — the facet is grouped on the name, not the id. */
  category?: string;
  /** `StockStatus` names, comma-separated. */
  stock?: string;

  /**
   * `OwnershipType` names, comma-separated — the TENURE axis.
   *
   * Two of the reference's tabs are not a kind: Equipment is Asset that is OWNED, and
   * Leased & Rented is anything LEASED. A tab is a kind filter plus this one.
   */
  tenure?: string;
  /** `near-expiry`, `expired`, `unusable`, `stolen` — not mutually exclusive, so its own axis. */
  alert?: string;
}

/**
 * One line of the stock ledger — mirrors `StockMovementListItem`.
 *
 * **There is no `isActive`, no `updatedAt` and no `updatedByName`**, and the absences are the
 * entity's shape rather than an omission: a movement is written once and stands forever, so the
 * three fields that describe being changed or hidden do not exist to send. The audit trail's own
 * row has the same shape for the same reason.
 */
export interface StockMovementListItem {
  id: string;

  /** When it was recorded. The ledger's own ordering, and the list's default sort. */
  occurredAt: string;

  /** Who recorded it, as their display name stood at that instant. A snapshot. */
  actorName: string | null;

  inventoryItemId: string;
  sku: string;
  /** The item's governed name, resolved through its type on every read. */
  itemName: string;
  unitCode: string;

  /**
   * The facility whose on-hand moved. NULL where none did.
   *
   * A licence seat sits nowhere, so receiving or writing one off moves no store's on-hand — see
   * `StockMovement.StorageFacilityId` on the server. The column renders an em dash: no facility
   * is a different fact from a facility holding nothing.
   */
  storageFacilityId: string | null;
  storageFacilityName: string | null;

  movementType: StockMovementType;

  /**
   * SIGNED — positive entering the facility, negative leaving it.
   *
   * Rendered with its sign rather than as a magnitude beside a direction, because the sign is
   * what makes the ledger sum to the truth: an item's balance at a facility is the sum of its
   * movements and nothing else.
   */
  quantity: number;

  /** What a unit was worth when it moved. Informational, and never re-read. */
  unitCostSnapshot: number | null;

  /** The lot this moved, where it was a quantity movement. */
  batchNumber: string | null;
  /** The unit this moved, where it was a serialized movement. */
  serialNumber: string | null;

  reason: string | null;
}

/**
 * The stock-ledger list query.
 *
 * **There is no `status` axis**, and the absence decides two things about the page rather than
 * being an omission: the shell derives both Show deleted and the folder-nav strip from whether
 * one is declared, and this table is append-only.
 */
export interface StockMovementListQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;

  /** Inventory-item ids, comma-separated. */
  item?: string;
  /** Storage-facility ids, comma-separated. */
  facility?: string;
  /** `StockMovementType` names, comma-separated. */
  movementType?: string;
  /** `in` / `out` — the SIGN of the quantity, which the type cannot express. */
  direction?: string;
}

/**
 * The figures that AGE, for one item — fetched on their own call so the register does not wait
 * for them.
 *
 * **Product call, 02 Oct 2026.** Every one of these is a date compared against TODAY, so none is
 * stored: a lot lapsing overnight moves them with no write anywhere in the system. A stored copy
 * was correct only as of the item's last recompute, which left an expired lot sitting in
 * Available on any item nobody had touched — with every figure around it agreeing.
 *
 * **Until these land the cells render a SKELETON, never a zero.** A muted zero is exactly what
 * "none expired" looks like, so showing one while the answer is still in flight is a wrong
 * answer rather than an absent one.
 */
export interface StockLevelAging {
  id: string;
  expired: number;
  nearExpiry: number;
  nearExpiryDays: number;
  delayedReturn: number;
  leaseExpiring: number;
  leaseExpired: number;
  leaseEndingDays: number;
  unusable: number;
  available: number;
  stockStatus: StockStatus;
  stockStatusLabel: string;
}

/**
 * The aging figures minus the key that identifies them — what merges onto a stored row.
 *
 * `id` is dropped deliberately: it is the ITEM's id on both sides, so spreading it would be
 * harmless today and is exactly the kind of thing that stops being harmless when one of the two
 * records grows a key of its own.
 */
export function withoutId(aging: StockLevelAging): Omit<StockLevelAging, 'id'> {
  const { id: _ignored, ...rest } = aging;
  return rest;
}
